import type { BusinessCustomerSummary } from '../api/schemas';

export type CustomerSegment = 'todos' | 'con-cita' | 'nuevos' | 'regulares' | 'inactivos' | 'en-pausa';
export type CustomerSort = 'reciente' | 'nombre' | 'visitas';

/** A first visit inside this window reads as a new client at the counter. */
const NEW_WINDOW_DAYS = 30;
/** Nothing booked and nothing visited for this long reads as gone quiet. */
const INACTIVE_AFTER_DAYS = 60;
/** Enough completed visits to call someone a regular. */
const REGULAR_MIN_VISITS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;

type StatusTone = 'good' | 'due' | 'off';
export type CustomerStatus = { label: string; tone?: StatusTone };

function daysSince(iso: string, now: Date): number {
  return (now.getTime() - new Date(iso).getTime()) / DAY_MS;
}

/**
 * The one word the row ends with. Precedence mirrors what the counter cares
 * about: a booked return beats everything, then a pause the team set (a trip
 * is not silence), then newness, then silence, then loyalty. Ordinary clients
 * get no word.
 */
export function customerStatus(
  customer: BusinessCustomerSummary,
  now: Date,
  isPaused = false,
): CustomerStatus | null {
  if (customer.upcomingVisits > 0) return { label: 'Con cita', tone: 'due' };
  if (isPaused) return { label: 'En pausa' };
  if (daysSince(customer.firstVisitAt, now) <= NEW_WINDOW_DAYS) return { label: 'Nuevo', tone: 'good' };
  if (daysSince(customer.lastVisitAt, now) > INACTIVE_AFTER_DAYS) return { label: 'Inactivo', tone: 'off' };
  if (customer.totalVisits >= REGULAR_MIN_VISITS) return { label: 'Regular' };
  return null;
}

/** `paused` holds the customers whose pause covers today: never inactive, and a segment of their own. */
export function segmentCustomers(
  customers: BusinessCustomerSummary[],
  segment: CustomerSegment,
  now: Date,
  paused: ReadonlySet<string> = new Set(),
): BusinessCustomerSummary[] {
  switch (segment) {
    case 'todos':
      return customers;
    case 'con-cita':
      return customers.filter((c) => c.upcomingVisits > 0);
    case 'nuevos':
      return customers.filter((c) => daysSince(c.firstVisitAt, now) <= NEW_WINDOW_DAYS);
    case 'regulares':
      return customers.filter((c) => c.totalVisits >= REGULAR_MIN_VISITS);
    case 'inactivos':
      return customers.filter(
        (c) => c.upcomingVisits === 0 && daysSince(c.lastVisitAt, now) > INACTIVE_AFTER_DAYS && !paused.has(c.customerId),
      );
    case 'en-pausa':
      return customers.filter((c) => paused.has(c.customerId));
  }
}

function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

export function matchesQuery(customer: BusinessCustomerSummary, query: string): boolean {
  const needle = fold(query.trim());
  if (!needle) return true;
  if (fold(customer.displayName).includes(needle)) return true;
  return Boolean(customer.maskedPhone && customer.maskedPhone.includes(needle));
}

const NAME_COLLATOR = new Intl.Collator('es');

export function sortCustomers(
  customers: BusinessCustomerSummary[],
  sort: CustomerSort,
): BusinessCustomerSummary[] {
  const copy = [...customers];
  switch (sort) {
    case 'reciente':
      return copy.sort((a, b) => b.lastVisitAt.localeCompare(a.lastVisitAt));
    case 'nombre':
      return copy.sort((a, b) => NAME_COLLATOR.compare(a.displayName, b.displayName));
    case 'visitas':
      return copy.sort((a, b) => b.totalVisits - a.totalVisits);
  }
}

/** Titles that precede a name ("Dra. Ana") and would give every doctor the same initial. */
const NAME_TITLE = /^(?:dra?|lcd[oa]|lic|sra?|srta)\.?\s+/i;

export function initialOf(name: string): string {
  const trimmed = name.trim();
  const withoutTitle = trimmed.replace(NAME_TITLE, '');
  return ((withoutTitle || trimmed)[0] ?? '?').toUpperCase();
}

const RELATIVE_FORMATTER = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
const MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('es-PA', { month: 'short', year: 'numeric' });

/** 'hoy', 'ayer', 'hace 3 días', then weeks, then months. */
export function relativeVisit(iso: string, now: Date): string {
  const days = Math.floor(daysSince(iso, now));
  if (days < 0) return 'programada';
  if (days < 14) return RELATIVE_FORMATTER.format(-days, 'day');
  if (days < 60) return RELATIVE_FORMATTER.format(-Math.floor(days / 7), 'week');
  return RELATIVE_FORMATTER.format(-Math.floor(days / 30), 'month');
}

/** 'cliente desde mar 2026' */
export function monthYear(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return MONTH_YEAR_FORMATTER.format(date);
}
